import React, { useState, useEffect, useContext } from "react";
import isEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { Logger, red } from "cms/utils/log/logger";
import { useAuth } from "cms/auth/hooks/use-auth";
import LabelledField from "components/labelled-field/index";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap/index";
import { ComponentHeroContext } from "cms/components/components/hero/edit/component-hero-context";
import { HeroImageEditor } from "cms/components/components/hero/edit/image/hero-image-editor";
import { isValidGuid, isStringEmpty } from "utils/empty-exists";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { cloneObjectsArray } from "utils/array/index";
import { FieldErrorFallback } from "cms/fields/layout/field-error";
import { ErrorBoundary } from "react-error-boundary";

/**
 *    This is the slide editor component
 * */
const HeroNewSlideEditor = () => {
    const { t } = useTranslation();
    const { token } = useAuth();

    const { list, item, index, updateItems } = useContext(ComponentHeroContext);

    const _title = item?.title;
    const _subtitle = item?.subtitle;
    const _description = item?.description;
    const _link = item?.link;
    const _image = item?.image;
    const _foreground_image = item?.foreground_image;

    const initial_image_guid = _image?.image ?? "";
    const _settings = _image?.settings ?? {};
    const initial_foreground_image_guid = _foreground_image?.image ?? "";
    const _foreground_image_settings = _foreground_image?.settings ?? {};

    const [loadingLink, setLoadingLink] = useState();
    const [imageLoading, setImageLoading] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);
    const [foregroundImageLoading, setForegroundImageLoading] = useState(false);
    const [foregroundImageFailed, setForegroundImageFailed] = useState(false);

    const [title, setTitle] = useState(_title);
    const [subtitle, setSubtitle] = useState(_subtitle);
    const [description, setDescription] = useState(_description);
    const [link, setLink] = useState(_link);
    const [image, setImage] = useState();
    const [settings, setSettings] = useState(_settings);
    const [foregroundImage, setForegroundImage] = useState();
    const [foregroundImageSettings, setForegroundImageSettings] = useState(_foreground_image_settings);

    useEffect(
        () => {
            // avoid updates if the description field has not changed
            if (!isEqual(_description, description)) {
                const nextSlides = cloneObjectsArray(list);
                const image_guid = image?.image ? image?.image : initial_image_guid;
                const foreground_image_guid = foregroundImage?.image ? foregroundImage?.image : initial_foreground_image_guid;
                // Logger.log(_image, image)
                nextSlides[index].title = title;
                nextSlides[index].link = link;
                nextSlides[index].image = {
                    image: image_guid,
                    settings,
                };
                nextSlides[index].foreground_image = {
                    image: foreground_image_guid,
                    foregroundImageSettings,
                };
                nextSlides[index].description = description;
                updateItems(nextSlides);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [description]
    );

    const onChangeTitle = (event) => {
        const title = event.target.value;
        // Logger.debug(`[onChangeTitle] title`, title)
        setTitle(title);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].title = title;
        updateItems(nextSlides);
    };

    const onChangeSubTitle = (event) => {
        const subtitle = event.target.value;
        // Logger.debug(`[onChangeTitle] title`, title)
        setSubtitle(subtitle);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].subtitle = subtitle;
        updateItems(nextSlides);
    };

    const onChangeDescription = (description) => {
        // Logger.debug(`[onChangeDescription] description`, description)
        // Due to stale closure with rich text editor - using useEffect()
        setDescription(description);
    };

    const onChangeLink = (link) => {
        // Logger.debug(`[onChangeLink] link`, link)
        setLink(link);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].link = link;
        updateItems(nextSlides);
    };

    const onRemoveLink = () => {
        const link = undefined;
        // Logger.debug(`[onRemoveLink] link`, link)
        setLink(link);
        const nextSlides = cloneObjectsArray(list);
        nextSlides[index].link = link;
        updateItems(nextSlides);
    };

    const setImageStateByType = (image, isForegroundImage) => {
        if (isForegroundImage) {
            setForegroundImage(image);
        } else {
            setImage(image);
        }
    };

    const setSettingsStateByType = (settings, isForegroundImage) => {
        if (isForegroundImage) {
            setForegroundImageSettings(settings);
        } else {
            setSettings(settings);
        }
    };

    const setImageLoadingStateByType = (value, isForegroundImage) => {
        if (isForegroundImage) {
            setForegroundImageLoading(value);
        } else {
            setImageLoading(value);
        }
    };

    const setImageFailedStateByType = (value, isForegroundImage) => {
        if (isForegroundImage) {
            setForegroundImageFailed(value);
        } else {
            setImageFailed(value);
        }
    };

    const updateImageByType = (newImageData, isForegroundImage) => {
        const nextSlides = cloneObjectsArray(list);
        if (isForegroundImage) {
            nextSlides[index].foreground_image = newImageData;
        } else {
            nextSlides[index].image = newImageData;
        }
        Logger.info(red(`[onChangeImageData] next slides ${index}`), nextSlides);
        updateItems(nextSlides);
        setImageFailedStateByType(false, isForegroundImage);
    };

    const onChangeImageData = (imageData, isForegroundImage = false) => {
        console.log("ONCHANGEIMAGEDATA... ISFOREGROUNDIMAGE = ", isForegroundImage);
        const { image, settings } = imageData; // image is object here
        setImageStateByType(image, isForegroundImage);
        setSettingsStateByType(settings, isForegroundImage);
        const { guid } = image || {};
        if (isStringEmpty(guid)) {
            Logger.warn("[onChangeImageData] image guid missing", index, image, guid);
        } else {
            const newImageData = {
                image: guid, // image is guid here
                settings,
            };
            updateImageByType(newImageData, isForegroundImage);
        }
    };

    const onRemoveImage = (isForegroundImage = false) => {
        Logger.debug("[onRemoveImage]");
        setImageStateByType(undefined, isForegroundImage);
        setSettingsStateByType(undefined, isForegroundImage);
        const newImageData = {
            image: undefined,
            settings: undefined,
        };
        updateImageByType(newImageData, isForegroundImage);
    };

    const fetchImage = async (guid, isForegroundImage = false) => {
        if (isValidGuid(guid)) {
            setImageLoadingStateByType(true, isForegroundImage);
            try {
                let currentImage = await SimpliCityAdminSDK.images.findOne(token, guid);
                setImageStateByType(currentImage, isForegroundImage);
                setImageLoadingStateByType(false, isForegroundImage);
            } catch (err) {
                console.error(err);
                setImageFailedStateByType(true, isForegroundImage);
            }
        } else {
            setImageLoadingStateByType(false, isForegroundImage);
            setImageFailedStateByType(false, isForegroundImage);
        }
    };

    useEffect(() => {
        fetchImage(initial_image_guid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial_image_guid]);

    useEffect(() => {
        fetchImage(initial_foreground_image_guid, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial_foreground_image_guid]);

    return (
        <div className="simplicity-editor-frame hero-editor-container">
            <div className="component-editor-image-wrapper">
                <div className="component-editor-image-details-wrapper-cell">

                    <LabelledField isRequired label={`${t("commons.image")} :`}>
                        <div className="component-editor-image-wrapper-cell">
                            {!imageLoading && (
                                <HeroImageEditor
                                    image={image}
                                    image_guid={image?.guid}
                                    settings={settings}
                                    onChangeImageData={onChangeImageData}
                                    onRemoveImage={onRemoveImage}
                                />
                            )}
                            {imageFailed && (
                                <>
                                    <HeroImageEditor
                                        image={undefined}
                                        image_guid={undefined}
                                        settings={settings}
                                        onChangeImageData={onChangeImageData}
                                        onRemoveImage={onRemoveImage}
                                    />
                                </>
                            )}
                        </div>
                    </LabelledField>

                    <LabelledField label={`${t("commons.title")} :`}>
                        {/*  Title  */}
                        <TextField
                            id="carousel-title"
                            fullWidth={true}
                            value={title}
                            placeholder={t("commons.addTitle")}
                            onChange={onChangeTitle}
                        />
                    </LabelledField>
                    <LabelledField label={`${t("commons.addSubtitle")} :`}>
                        {/*  Sub-Title  */}
                        <TextField
                            id="carousel-title"
                            fullWidth={true}
                            value={subtitle}
                            placeholder={t("commons.addSubtitle")}
                            onChange={onChangeSubTitle}
                        />
                    </LabelledField>

                    <LabelledField label={t("commons.description")}>
                        {/*  Description  */}
                        <ErrorBoundary
                            FallbackComponent={FieldErrorFallback}
                            onReset={() => {
                                location.reload();
                            }}
                        >
                            <SimplicityComposerEnsureTipTap
                                richtext={description}
                                onChange={onChangeDescription}
                            />
                        </ErrorBoundary>
                    </LabelledField>

                    <LabelledField
                        label={`${t("commons.link")} :`}
                        className="hero-editor-link"
                    >
                        {/*  Link  */}
                        <div style={{ flex: "1" }}>
                            <LinkSelectorWrapper
                                link={link}
                                setLink={setLink}
                                onLinkChange={(link) => onChangeLink(link)}
                                onLinkRemove={() => onRemoveLink()}
                                disabled={false}
                                loadingLink={loadingLink}
                                setLoadingLink={setLoadingLink}
                            />
                        </div>
                    </LabelledField>

                    <LabelledField label={`${t("commons.foregroundImage")} :`}>
                        <div className="foreground-image component-editor-image-wrapper-cell">
                            {!foregroundImageLoading && (
                                <HeroImageEditor
                                    image={foregroundImage}
                                    image_guid={foregroundImage?.guid}
                                    settings={foregroundImageSettings}
                                    onChangeImageData={(imageData) => onChangeImageData(imageData, true)}
                                    onRemoveImage={() => onRemoveImage(true)}
                                />
                            )}
                            {foregroundImageFailed && (
                                <>
                                    <HeroImageEditor
                                        image={undefined}
                                        image_guid={undefined}
                                        settings={foregroundImageSettings}
                                        onChangeImageData={(imageData) => onChangeImageData(imageData, true)}
                                        onRemoveImage={() => onRemoveImage(true)}
                                    />
                                </>
                            )}
                        </div>
                    </LabelledField>
                </div>
            </div>
        </div>
    );
};
HeroNewSlideEditor.displayName = "HeroNewSlideEditor";
export { HeroNewSlideEditor };
